import { useState } from 'react';
import {
  X,
  FileText,
  FileSpreadsheet,
  Download,
  Printer,
  Mail,
  MessageCircle,
  Loader2,
  Check
} from 'lucide-react';
import { toast } from 'react-toastify';
import { exportExpenseReport, emailExpenseReport } from '../../../api/expenses';

const ExpenseReportModal = ({ isOpen, onClose, filters, activeDateRange, expenseCount, isAllTime }) => {
  const [format, setFormat] = useState('pdf');
  const [action, setAction] = useState('download');
  const [loading, setLoading] = useState(false);
  const [emailData, setEmailData] = useState({ recipientEmail: '', recipientName: '' });
  const [whatsappPhone, setWhatsappPhone] = useState('');

  if (!isOpen) return null;

  const buildExportParams = () => {
    const params = { format, module: 'wholesale' };
    if (filters.status) params.status = filters.status;
    if (filters.category_id) params.category_id = filters.category_id;
    if (activeDateRange.start_date) params.start_date = activeDateRange.start_date;
    if (activeDateRange.end_date) params.end_date = activeDateRange.end_date;
    if (isAllTime) params.period_type = 'forever';
    return params;
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const params = buildExportParams();
      const blob = await exportExpenseReport(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expense_report_${Date.now()}.${format === 'pdf' ? 'pdf' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
      onClose();
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    setLoading(true);
    try {
      const params = { ...buildExportParams(), format: 'pdf' };
      const blob = await exportExpenseReport(params);
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
      toast.success('Print dialog opened');
      onClose();
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to generate report for printing');
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async () => {
    if (!emailData.recipientEmail) {
      toast.error('Please enter a recipient email');
      return;
    }
    setLoading(true);
    try {
      await emailExpenseReport({
        recipientEmail: emailData.recipientEmail,
        recipientName: emailData.recipientName,
        format,
        filters: {
          module: 'wholesale',
          status: filters.status || undefined,
          category_id: filters.category_id || undefined,
          start_date: activeDateRange.start_date || undefined,
          end_date: activeDateRange.end_date || undefined,
          period_type: isAllTime ? 'forever' : undefined
        }
      });
      toast.success(`Report sent to ${emailData.recipientEmail}`);
      onClose();
    } catch (error) {
      console.error('Email error:', error);
      toast.error(error.response?.data?.message || 'Failed to send report via email');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = async () => {
    if (!whatsappPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    setLoading(true);
    try {
      // Download the file first, then share via WhatsApp with a message
      const params = buildExportParams();
      const blob = await exportExpenseReport(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expense_report_${Date.now()}.${format === 'pdf' ? 'pdf' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Open WhatsApp with pre-filled message
      const phone = whatsappPhone.replace(/\D/g, '');
      const normalizedPhone = phone.startsWith('0') ? `234${phone.slice(1)}` : phone.startsWith('234') ? phone : `234${phone}`;
      const periodLabel = activeDateRange.start_date && activeDateRange.end_date
        ? `${activeDateRange.start_date} to ${activeDateRange.end_date}`
        : 'All Time';
      const message = encodeURIComponent(
        `Wholesale Expense Report\nPeriod: ${periodLabel}\nTotal Expenses: ${expenseCount}\nFormat: ${format.toUpperCase()}\n\nPlease find the attached report file.`
      );
      window.open(`https://wa.me/${normalizedPhone}?text=${message}`, '_blank');

      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded. WhatsApp opened - please attach the file.');
      onClose();
    } catch (error) {
      console.error('WhatsApp error:', error);
      toast.error('Failed to generate report for WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    switch (action) {
      case 'download': return handleDownload();
      case 'print': return handlePrint();
      case 'email': return handleEmail();
      case 'whatsapp': return handleWhatsApp();
      default: return handleDownload();
    }
  };

  const formatOptions = [
    {
      id: 'pdf',
      name: 'PDF',
      description: 'Best for printing and official records',
      icon: FileText,
      color: 'violet'
    },
    {
      id: 'csv',
      name: 'CSV',
      description: 'Best for spreadsheets and data analysis',
      icon: FileSpreadsheet,
      color: 'emerald'
    }
  ];

  const actionOptions = [
    { id: 'download', name: 'Download', icon: Download, description: 'Save file to your device' },
    { id: 'print', name: 'Print', icon: Printer, description: 'Open print dialog' },
    { id: 'email', name: 'Email', icon: Mail, description: 'Send report via email' },
    { id: 'whatsapp', name: 'WhatsApp', icon: MessageCircle, description: 'Share via WhatsApp' }
  ];

  const periodLabel = activeDateRange.start_date && activeDateRange.end_date
    ? `${new Date(activeDateRange.start_date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })} - ${new Date(activeDateRange.end_date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}`
    : 'All Time';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Generate Expense Report</h2>
            <p className="text-sm text-gray-600 mt-0.5">
              {expenseCount} expense{expenseCount !== 1 ? 's' : ''} | {periodLabel}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Report Format</label>
            <div className="grid grid-cols-2 gap-3">
              {formatOptions.map((opt) => {
                const Icon = opt.icon;
                const isSelected = format === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setFormat(opt.id)}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      isSelected
                        ? opt.color === 'violet'
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-md ${
                        opt.color === 'violet' ? 'bg-violet-100' : 'bg-emerald-100'
                      }`}>
                        <Icon className={`w-4 h-4 ${
                          opt.color === 'violet' ? 'text-violet-600' : 'text-emerald-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-gray-900 text-sm">{opt.name}</span>
                        {isSelected && (
                          <Check className={`w-4 h-4 inline ml-1 ${
                            opt.color === 'violet' ? 'text-violet-500' : 'text-emerald-500'
                          }`} />
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{opt.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Action</label>
            <div className="grid grid-cols-2 gap-2">
              {actionOptions.map((opt) => {
                const Icon = opt.icon;
                const isSelected = action === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setAction(opt.id)}
                    className={`flex items-center gap-2 p-3 border-2 rounded-lg text-left transition-all ${
                      isSelected
                        ? 'border-emerald-600 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isSelected ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <div>
                      <span className={`text-sm font-medium ${isSelected ? 'text-emerald-700' : 'text-gray-700'}`}>
                        {opt.name}
                      </span>
                      <p className="text-xs text-gray-500">{opt.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Email Fields */}
          {action === 'email' && (
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email *</label>
                <input
                  type="email"
                  value={emailData.recipientEmail}
                  onChange={(e) => setEmailData({ ...emailData, recipientEmail: e.target.value })}
                  placeholder="recipient@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Name</label>
                <input
                  type="text"
                  value={emailData.recipientName}
                  onChange={(e) => setEmailData({ ...emailData, recipientName: e.target.value })}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          )}

          {/* WhatsApp Fields */}
          {action === 'whatsapp' && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input
                type="tel"
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value)}
                placeholder="08012345678"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                The report will be downloaded first, then WhatsApp will open for you to attach the file.
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {action === 'download' && <Download className="w-4 h-4" />}
                  {action === 'print' && <Printer className="w-4 h-4" />}
                  {action === 'email' && <Mail className="w-4 h-4" />}
                  {action === 'whatsapp' && <MessageCircle className="w-4 h-4" />}
                  {action === 'download' && 'Download Report'}
                  {action === 'print' && 'Print Report'}
                  {action === 'email' && 'Send Email'}
                  {action === 'whatsapp' && 'Share via WhatsApp'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReportModal;
